import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Trophy, TrendingUp, TrendingDown, BarChart3, Filter, Search, ScatterChart as Scatter, Grid3x3, Layers } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const fmtPct = (n) => n == null ? "—" : (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
const colorPct = (n) => n == null ? "#666" : n > 20 ? "#22c55e" : n > 10 ? "#84cc16" : n > 0 ? "#aaa" : "#ef4444";

const SECTOR_COLORS = {
  US_LARGE: "#3b82f6", US_TOTAL: "#3b82f6", US_LARGE_EQUAL: "#3b82f6",
  TECH: "#a855f7", MAG7: "#fb923c", TECH_LEV: "#f97316",
  GROWTH: "#22c55e", VALUE: "#10b981", DIVIDEND: "#84cc16",
  SMALL: "#06b6d4", MID: "#06b6d4",
  INTL: "#f59e0b", DEV_INTL: "#f59e0b", EMERGING: "#f59e0b",
  JAPAN: "#ec4899", CHINA: "#dc2626", INDIA: "#f59e0b", EUROPE: "#fbbf24",
  SECTOR: "#94a3b8",
  BONDS: "#6b7280", LONG_BONDS: "#4b5563", MID_BONDS: "#4b5563",
  SHORT_BONDS: "#9ca3af", TIPS: "#6b7280", JUNK: "#9ca3af",
  GOLD: "#fbbf24", SILVER: "#94a3b8", OIL: "#000", COMMODITIES: "#a16207",
  REIT: "#10b981", CRYPTO: "#f97316", ALLOC: "#a3a3a3",
  INNOVATION: "#a855f7", WORLD: "#3b82f6",
};

// ──────────────────────────────────────────────────────────────────────────
// VISUALIZERS
// ──────────────────────────────────────────────────────────────────────────

function RiskReturnScatter({ etfs, period, hoveredTicker, setHoveredTicker, selectedTickers, setSelectedTickers }) {
  const valid = etfs.filter(e => e[`${period}_apy`] != null && e[`${period}_dd`] != null);
  if (valid.length === 0) return null;

  const W = 700, H = 400, PAD = 50;
  const apys = valid.map(e => e[`${period}_apy`]);
  const dds = valid.map(e => e[`${period}_dd`]);
  const minApy = Math.min(...apys, 0);
  const maxApy = Math.max(...apys, 0);
  const maxDd = Math.max(...dds, 10);

  const xScale = (dd) => PAD + (dd / maxDd) * (W - 2 * PAD);
  const yScale = (apy) => H - PAD - ((apy - minApy) / (maxApy - minApy)) * (H - 2 * PAD);

  // Quadrant labels
  const xMid = W / 2;
  const yMid = yScale((minApy + maxApy) / 2);

  return (
    <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Scatter size={16} style={{ color: "#a855f7" }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Risk vs Return Scatter — {period}</div>
        <span style={{ fontSize: 10, color: "#888" }}>(top-left = best)</span>
      </div>
      <svg width={W} height={H} style={{ display: "block", margin: "0 auto" }}>
        {/* Grid */}
        <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} stroke="#2a2d39" />
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#2a2d39" />
        {/* Quadrant divider */}
        <line x1={xMid} y1={PAD} x2={xMid} y2={H - PAD} stroke="#1a1d29" strokeDasharray="4,4" />
        <line x1={PAD} y1={yMid} x2={W - PAD} y2={yMid} stroke="#1a1d29" strokeDasharray="4,4" />
        {/* Quadrant labels */}
        <text x={PAD + 10} y={PAD + 14} fill="#22c55e" fontSize="10" fontWeight="600">🏆 HIGH RETURN, LOW RISK</text>
        <text x={W - PAD - 10} y={PAD + 14} fill="#facc15" fontSize="10" fontWeight="600" textAnchor="end">⚡ HIGH RETURN, HIGH RISK</text>
        <text x={PAD + 10} y={H - PAD - 6} fill="#aaa" fontSize="10" fontWeight="600">😴 LOW EVERYTHING</text>
        <text x={W - PAD - 10} y={H - PAD - 6} fill="#ef4444" fontSize="10" fontWeight="600" textAnchor="end">💩 BAD</text>

        {/* Axes labels */}
        <text x={W / 2} y={H - 12} fill="#888" fontSize="11" textAnchor="middle">Max Drawdown (%) →</text>
        <text x={15} y={H / 2} fill="#888" fontSize="11" textAnchor="middle" transform={`rotate(-90, 15, ${H/2})`}>APY (%) →</text>

        {/* Dots */}
        {valid.map(e => {
          const x = xScale(e[`${period}_dd`]);
          const y = yScale(e[`${period}_apy`]);
          const sectorColor = SECTOR_COLORS[e.sector] || "#888";
          const isHovered = hoveredTicker === e.ticker;
          const isSelected = selectedTickers.includes(e.ticker);
          const r = isHovered || isSelected ? 8 : 5;
          return (
            <g key={e.ticker}
               onMouseEnter={() => setHoveredTicker(e.ticker)}
               onMouseLeave={() => setHoveredTicker(null)}
               onClick={() => {
                 if (selectedTickers.includes(e.ticker)) {
                   setSelectedTickers(selectedTickers.filter(t => t !== e.ticker));
                 } else if (selectedTickers.length < 5) {
                   setSelectedTickers([...selectedTickers, e.ticker]);
                 }
               }}
               style={{ cursor: "pointer" }}>
              <circle cx={x} cy={y} r={r} fill={sectorColor}
                stroke={isSelected ? "#fff" : isHovered ? "#fff" : "none"}
                strokeWidth={isSelected ? 2 : 1}
                opacity={isHovered || isSelected ? 1 : 0.7}/>
              {(isHovered || isSelected) && (
                <text x={x + 10} y={y - 5} fill="#fff" fontSize="11" fontWeight="700">
                  {e.ticker}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hoveredTicker && (() => {
        const e = valid.find(v => v.ticker === hoveredTicker);
        if (!e) return null;
        return (
          <div style={{ marginTop: 8, padding: 10, background: "#0a0c14", borderRadius: 6, fontSize: 12, display: "flex", gap: 16, alignItems: "center" }}>
            <span style={{ color: "#fff", fontWeight: 700 }}>{e.ticker}</span>
            <span style={{ color: "#aaa" }}>{e.name}</span>
            <span style={{ color: colorPct(e[`${period}_apy`]), fontWeight: 700 }}>APY {fmtPct(e[`${period}_apy`])}</span>
            <span style={{ color: "#ef4444" }}>DD -{e[`${period}_dd`]?.toFixed(1)}%</span>
            <span style={{ color: "#3b82f6" }}>Sharpe {e[`${period}_sharpe`]?.toFixed(2)}</span>
          </div>
        );
      })()}
      <div style={{ fontSize: 10, color: "#666", marginTop: 8, textAlign: "center" }}>
        💡 Click στις τελείες για compare (max 5) — hover για details
      </div>
    </div>
  );
}

function SectorHeatmap({ etfs, period }) {
  const sectorAggr = {};
  etfs.forEach(e => {
    const apy = e[`${period}_apy`];
    if (apy == null) return;
    if (!sectorAggr[e.sector]) sectorAggr[e.sector] = { sector: e.sector, apys: [], etfs: [] };
    sectorAggr[e.sector].apys.push(apy);
    sectorAggr[e.sector].etfs.push(e.ticker);
  });
  const sectorList = Object.values(sectorAggr).map(s => ({
    sector: s.sector,
    avg: s.apys.reduce((a,b) => a+b, 0) / s.apys.length,
    max: Math.max(...s.apys),
    min: Math.min(...s.apys),
    count: s.apys.length,
    etfs: s.etfs,
  })).sort((a,b) => b.avg - a.avg);

  const maxAvg = Math.max(...sectorList.map(s => s.avg));
  const minAvg = Math.min(...sectorList.map(s => s.avg));

  return (
    <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Grid3x3 size={16} style={{ color: "#22c55e" }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Sector Heatmap — {period} Avg APY</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 6 }}>
        {sectorList.map(s => {
          const sectorColor = SECTOR_COLORS[s.sector] || "#888";
          // Heat intensity: green for high, red for low
          const range = maxAvg - minAvg || 1;
          const norm = (s.avg - minAvg) / range;
          const heatColor = norm > 0.66 ? "#22c55e" : norm > 0.33 ? "#facc15" : "#ef4444";
          return (
            <div key={s.sector} style={{
              background: `linear-gradient(135deg, ${heatColor}22, #0a0c14)`,
              border: `1px solid ${heatColor}55`,
              borderRadius: 8, padding: 10,
            }}>
              <div style={{ fontSize: 10, color: sectorColor, fontWeight: 700 }}>{s.sector}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: heatColor }}>{fmtPct(s.avg)}</div>
              <div style={{ fontSize: 9, color: "#888" }}>
                {s.count} ETFs · range {s.min.toFixed(1)}-{s.max.toFixed(1)}%
              </div>
              <div style={{ fontSize: 9, color: "#666", marginTop: 4, maxHeight: 32, overflow: "hidden" }}>
                {s.etfs.slice(0, 5).join(", ")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompareCard({ etfs, selectedTickers, setSelectedTickers }) {
  if (selectedTickers.length === 0) return null;
  const selected = selectedTickers.map(t => etfs.find(e => e.ticker === t)).filter(Boolean);
  if (selected.length === 0) return null;

  return (
    <div style={{ background: "#13161f", border: "1px solid #a855f755", borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Layers size={16} style={{ color: "#a855f7" }} />
          <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Compare ({selected.length}/5)</div>
        </div>
        <button onClick={() => setSelectedTickers([])} style={{
          background: "transparent", border: "1px solid #2a2d39", color: "#aaa",
          padding: "3px 8px", borderRadius: 4, cursor: "pointer", fontSize: 10,
        }}>Clear</button>
      </div>
      {/* Multi-period bars */}
      <div style={{ display: "grid", gridTemplateColumns: "100px repeat(4, 1fr)", gap: 8, fontSize: 11 }}>
        <div></div>
        <div style={{ color: "#888", textAlign: "center", fontWeight: 600 }}>1Y</div>
        <div style={{ color: "#888", textAlign: "center", fontWeight: 600 }}>3Y</div>
        <div style={{ color: "#888", textAlign: "center", fontWeight: 600 }}>5Y</div>
        <div style={{ color: "#888", textAlign: "center", fontWeight: 600 }}>10Y</div>
        {selected.map(e => {
          const sectorColor = SECTOR_COLORS[e.sector] || "#888";
          const allApys = ["1Y", "3Y", "5Y", "10Y"].map(p => e[`${p}_apy`] || 0);
          const max = Math.max(...allApys, 10);
          return (
            <React.Fragment key={e.ticker}>
              <div style={{ color: "#fff", fontWeight: 700, alignSelf: "center" }}>
                <div>{e.ticker}</div>
                <div style={{ fontSize: 9, color: sectorColor }}>{e.sector}</div>
              </div>
              {["1Y", "3Y", "5Y", "10Y"].map(p => {
                const apy = e[`${p}_apy`];
                const w = apy != null && apy > 0 ? (apy / max * 100) : 0;
                return (
                  <div key={p} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ flex: 1, height: 22, background: "#0a0c14", borderRadius: 3, overflow: "hidden", position: "relative" }}>
                      {apy != null && (
                        <>
                          <div style={{
                            width: `${Math.max(0, w)}%`, height: "100%",
                            background: apy > 0 ? sectorColor : "#ef4444",
                          }}/>
                          <div style={{
                            position: "absolute", left: 4, top: 4, fontSize: 10,
                            color: "#fff", fontWeight: 700, textShadow: "0 0 3px #000",
                          }}>{fmtPct(apy)}</div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default function EtfAuditTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("10Y");
  const [sector, setSector] = useState("ALL");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("table"); // "table" | "scatter" | "heatmap"
  const [hoveredTicker, setHoveredTicker] = useState(null);
  const [selectedTickers, setSelectedTickers] = useState([]);

  const fetchData = useCallback(async () => {
    if (!token) { setError("Not logged in"); setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/skynet-brain/etf-audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) { setError("Session expired"); setLoading(false); return; }
      const j = await r.json();
      if (j.error) setError(j.error);
      else { setData(j); setError(null); }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!data?.etfs) return [];
    let list = [...data.etfs];
    if (sector !== "ALL") list = list.filter(e => e.sector === sector);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e => e.ticker.toLowerCase().includes(q) || e.name?.toLowerCase().includes(q));
    }
    list = list.filter(e => e[`${period}_apy`] != null);
    list.sort((a, b) => (b[`${period}_apy`] || -999) - (a[`${period}_apy`] || -999));
    return list;
  }, [data, period, sector, search]);

  const sectors = useMemo(() => {
    if (!data?.etfs) return [];
    return [...new Set(data.etfs.map(e => e.sector))].sort();
  }, [data]);

  const maxApy = useMemo(() => {
    if (!filtered.length) return 50;
    return Math.max(...filtered.map(e => e[`${period}_apy`] || 0), 10);
  }, [filtered, period]);

  if (error && !data) return <div style={{ padding: 40, color: "#ef4444", textAlign: "center" }}>{error}</div>;
  if (loading && !data) return <div style={{ padding: 40, color: "#888", textAlign: "center" }}>Loading ETF audit…</div>;
  if (!data) return null;

  return (
    <div style={{ padding: "16px 20px", color: "#eee", maxWidth: 1500, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f1218, #1a1d29)", border: "1px solid #2a2d39", borderRadius: 14, padding: 18, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <Trophy size={22} style={{ color: "#facc15" }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>ETF Audit</div>
          <span style={{ fontSize: 11, padding: "3px 8px", background: "#facc1533", color: "#facc15", borderRadius: 4, fontWeight: 600 }}>
            {data.n_etfs} ETFs · 4 periods
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#666" }}>Adjusted prices (with dividends) — {data.as_of?.substring(0, 10)}</div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        {/* Period selector */}
        <div style={{ display: "flex", gap: 4 }}>
          {["1Y", "3Y", "5Y", "10Y"].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{
                padding: "6px 14px",
                background: period === p ? "#3b82f6" : "#1a1d29",
                color: period === p ? "#fff" : "#aaa",
                border: "1px solid " + (period === p ? "#3b82f6" : "#2a2d39"),
                borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12,
              }}>
              {p}
            </button>
          ))}
        </div>

        {/* Sector filter */}
        <select value={sector} onChange={e => setSector(e.target.value)}
          style={{ background: "#0a0c14", border: "1px solid #2a2d39", color: "#fff", padding: "6px 10px", borderRadius: 6, fontSize: 12 }}>
          <option value="ALL">All sectors ({data.etfs?.length})</option>
          {sectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* Search */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#0a0c14", border: "1px solid #2a2d39", borderRadius: 6, padding: "0 8px" }}>
          <Search size={12} style={{ color: "#666" }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="ticker or name"
            style={{ background: "transparent", border: "none", color: "#fff", padding: "6px 4px", fontSize: 12, outline: "none", width: 160 }}/>
        </div>

        {/* View switcher */}
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          {[
            { id: "table",   label: "Table",   icon: BarChart3 },
            { id: "scatter", label: "Scatter", icon: Scatter },
            { id: "heatmap", label: "Heatmap", icon: Grid3x3 },
          ].map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              style={{
                padding: "6px 12px", display: "flex", alignItems: "center", gap: 4,
                background: view === v.id ? "#a855f7" : "#1a1d29",
                color: view === v.id ? "#fff" : "#aaa",
                border: "1px solid " + (view === v.id ? "#a855f7" : "#2a2d39"),
                borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600,
              }}>
              <v.icon size={12} /> {v.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, color: "#666", marginBottom: 12 }}>
        {filtered.length} results · sorted by {period} APY
      </div>

      {/* Compare card if any selected */}
      <CompareCard etfs={filtered} selectedTickers={selectedTickers} setSelectedTickers={setSelectedTickers} />

      {/* Scatter view */}
      {view === "scatter" && (
        <RiskReturnScatter etfs={filtered} period={period}
          hoveredTicker={hoveredTicker} setHoveredTicker={setHoveredTicker}
          selectedTickers={selectedTickers} setSelectedTickers={setSelectedTickers} />
      )}

      {/* Heatmap view */}
      {view === "heatmap" && <SectorHeatmap etfs={filtered} period={period} />}

      {/* Top 3 podium — show in table view */}
      {view === "table" && filtered.length >= 3 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {filtered.slice(0, 3).map((etf, i) => {
            const apy = etf[`${period}_apy`];
            const dd = etf[`${period}_dd`];
            const sharpe = etf[`${period}_sharpe`];
            const sectorColor = SECTOR_COLORS[etf.sector] || "#888";
            const emoji = ["🥇", "🥈", "🥉"][i];
            return (
              <div key={etf.ticker} style={{
                background: i === 0 ? "linear-gradient(135deg, #facc1522, #1a1d29)" : "#13161f",
                border: `1px solid ${i === 0 ? "#facc1555" : "#2a2d39"}`,
                borderRadius: 12, padding: 16,
              }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>{emoji}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{etf.ticker}</div>
                <div style={{ fontSize: 10, color: "#888", marginBottom: 6 }}>{etf.name}</div>
                <span style={{ fontSize: 9, padding: "2px 6px", background: sectorColor + "22", color: sectorColor, borderRadius: 3 }}>
                  {etf.sector}
                </span>
                <div style={{ fontSize: 26, fontWeight: 800, color: colorPct(apy), marginTop: 8 }}>
                  {fmtPct(apy)}
                </div>
                <div style={{ fontSize: 10, color: "#888" }}>{period} APY</div>
                <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 10 }}>
                  <span><span style={{ color: "#666" }}>DD:</span> <span style={{ color: "#ef4444" }}>-{dd?.toFixed(1)}%</span></span>
                  <span><span style={{ color: "#666" }}>Sharpe:</span> <span style={{ color: "#3b82f6" }}>{sharpe?.toFixed(2)}</span></span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full table — only in table view */}
      {view === "table" && (
      <div style={{ background: "#13161f", border: "1px solid #2a2d39", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "#fff", borderBottom: "1px solid #2a2d39" }}>
          📊 Leaderboard ({filtered.length} ETFs)
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#0a0c14", color: "#666", textTransform: "uppercase", fontSize: 9 }}>
              <th style={{ padding: 8, textAlign: "left", width: 30 }}>#</th>
              <th style={{ padding: 8, textAlign: "left" }}>Ticker</th>
              <th style={{ padding: 8, textAlign: "left" }}>Name</th>
              <th style={{ padding: 8, textAlign: "left" }}>Sector</th>
              <th style={{ padding: 8, textAlign: "right" }}>1Y APY</th>
              <th style={{ padding: 8, textAlign: "right" }}>3Y APY</th>
              <th style={{ padding: 8, textAlign: "right" }}>5Y APY</th>
              <th style={{ padding: 8, textAlign: "right" }}>10Y APY</th>
              <th style={{ padding: 8, textAlign: "right" }}>Max DD</th>
              <th style={{ padding: 8, textAlign: "right" }}>Sharpe</th>
              <th style={{ padding: 8, textAlign: "left", width: 200 }}>{period} Bar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((etf, i) => {
              const apy = etf[`${period}_apy`];
              const dd = etf[`${period}_dd`];
              const sharpe = etf[`${period}_sharpe`];
              const sectorColor = SECTOR_COLORS[etf.sector] || "#888";
              const barWidth = apy > 0 ? (apy / maxApy * 100) : 0;
              return (
                <tr key={etf.ticker} style={{ borderTop: "1px solid #1a1d29" }}>
                  <td style={{ padding: 8, color: "#666", fontWeight: 600 }}>{i + 1}</td>
                  <td style={{ padding: 8, color: "#fff", fontWeight: 700 }}>{etf.ticker}</td>
                  <td style={{ padding: 8, color: "#aaa", fontSize: 11, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {etf.name}
                  </td>
                  <td style={{ padding: 8 }}>
                    <span style={{ fontSize: 9, padding: "2px 6px", background: sectorColor + "22", color: sectorColor, borderRadius: 3 }}>
                      {etf.sector}
                    </span>
                  </td>
                  <td style={{ padding: 8, textAlign: "right", color: colorPct(etf["1Y_apy"]) }}>{fmtPct(etf["1Y_apy"])}</td>
                  <td style={{ padding: 8, textAlign: "right", color: colorPct(etf["3Y_apy"]) }}>{fmtPct(etf["3Y_apy"])}</td>
                  <td style={{ padding: 8, textAlign: "right", color: colorPct(etf["5Y_apy"]) }}>{fmtPct(etf["5Y_apy"])}</td>
                  <td style={{ padding: 8, textAlign: "right", color: colorPct(etf["10Y_apy"]), fontWeight: 700 }}>{fmtPct(etf["10Y_apy"])}</td>
                  <td style={{ padding: 8, textAlign: "right", color: "#ef4444" }}>-{dd?.toFixed(1)}%</td>
                  <td style={{ padding: 8, textAlign: "right", color: sharpe > 1 ? "#22c55e" : "#888" }}>{sharpe?.toFixed(2) || "—"}</td>
                  <td style={{ padding: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ flex: 1, height: 16, background: "#0a0c14", borderRadius: 3, overflow: "hidden" }}>
                        {apy != null && (
                          <div style={{
                            width: `${Math.max(0, barWidth)}%`,
                            height: "100%",
                            background: apy > 0 ?
                              `linear-gradient(90deg, ${sectorColor}, ${sectorColor}dd)` : "#ef4444",
                          }}/>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      <div style={{ marginTop: 16, padding: 10, fontSize: 10, color: "#555", textAlign: "center" }}>
        Adjusted close prices · max drawdown peak-to-trough · Sharpe = annualized risk-adjusted return
      </div>
    </div>
  );
}
